
import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { SEOHead } from "@/components/SEOHead";
import { startOfDay, addDays, endOfDay } from "date-fns";
import { Flight } from "@/types/flight";
import { fetchUnifiedFlights } from "@/services/flights/unifiedFlightQuery";


import { FlightPageHeader } from "@/components/FlightPageHeader";
import { FlightTabs } from "@/components/FlightTabs";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";

import { useRealtimeFlights } from "@/hooks/useRealtimeFlights";
import { Loader2 } from "lucide-react";
import { OrganizationLinks } from "@/components/OrganizationLinks";

export default function FlightBoardPage() {
  const [searchTerm, setSearchTerm] = useState("");
  
  // Enable real-time updates for the flight board
  useRealtimeFlights();
  
  // Fetch flights for today and tomorrow from unified view (flights + cache)
  const { data: flights = [], isLoading } = useQuery({
    queryKey: ['flights'],
    queryFn: async () => {
      const today = startOfDay(new Date());
      const dayAfterTomorrow = startOfDay(addDays(new Date(), 2));
      
      return fetchUnifiedFlights({
        gteDate: today.toISOString(),
        ltDate: dayAfterTomorrow.toISOString(),
      });
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  
  console.log('FlightBoardPage - flights from query:', flights);
  console.log('FlightBoardPage - flights length:', flights?.length || 0);
  console.log('FlightBoardPage - isLoading:', isLoading);
  
  // Log some sample flight dates to understand the data
  if (flights.length > 0) {
    console.log('Sample flight departure dates:');
    flights.slice(0, 5).forEach((flight, index) => {
      console.log(`Flight ${index + 1}: ${flight.flight_number} - Departure: ${flight.scheduled_departure}, Arrival: ${flight.scheduled_arrival}`);
      console.log(`  - Departure Date object: ${new Date(flight.scheduled_departure)}`);
      console.log(`  - Arrival Date object: ${new Date(flight.scheduled_arrival)}`);
    });
  }
  
  // Memoize filtered flights to prevent unnecessary recalculations
  const filteredFlights = useMemo(() => {
    if (!searchTerm.trim()) return flights;
    
    const searchLower = searchTerm.toLowerCase();
    return flights.filter(flight => {
      return (
        flight.flight_number.toLowerCase().includes(searchLower) ||
        flight.departure_city?.name.toLowerCase().includes(searchLower) ||
        flight.arrival_city?.name.toLowerCase().includes(searchLower) ||
        flight.organization?.name.toLowerCase().includes(searchLower)
      );
    });
  }, [flights, searchTerm]);
  
  console.log('FlightBoardPage - filteredFlights length:', filteredFlights?.length || 0);
  
  // Memoize today's flights calculation - matching /ang route logic (today + tomorrow)
  const todaysFlights = useMemo(() => {
    // Match OrganizationFlightsPage date range: today through tomorrow
    const today = startOfDay(new Date());
    const tomorrow = startOfDay(addDays(new Date(), 1));
    const endOfTomorrow = endOfDay(tomorrow);

    console.log('=== LANDING PAGE DEBUG ===');
    console.log('Total flights fetched:', filteredFlights?.length || 0);
    console.log('Date range (matching /ang):', { 
      today: today.toISOString(), 
      endOfTomorrow: endOfTomorrow.toISOString() 
    });
    console.log('First 5 flights (raw):', filteredFlights.slice(0, 5).map(f => ({
      flight_number: f.flight_number,
      scheduled_departure: f.scheduled_departure,
      scheduled_arrival: f.scheduled_arrival,
      organization: f.organization?.code,
    })));

    const result = filteredFlights.filter(flight => {
      const departureDate = new Date(flight.scheduled_departure);
      
      // Match /ang logic: filter by departure date between today and end of tomorrow
      return departureDate >= today && departureDate <= endOfTomorrow;
    });
    
    console.log('Flights matching today+tomorrow filter:', result.map(f => ({
      flight_number: f.flight_number,
      scheduled_departure: f.scheduled_departure,
      organization: f.organization?.code,
    })));
    console.log('Total flights (today+tomorrow):', result.length);
    console.log('================================');
    
    return result;
  }, [filteredFlights]);

  // Also create a September 2025 flights filter for PNG Air schedule verification
  const septemberFlights = useMemo(() => {
    if (!filteredFlights?.length) return [];
    
    const septemberStart = new Date('2025-09-01T00:00:00Z');
    const septemberEnd = new Date('2025-09-30T23:59:59Z');
    
    const sepFlights = filteredFlights.filter((flight) => {
      const departureDate = new Date(flight.scheduled_departure);
      return departureDate >= septemberStart && departureDate <= septemberEnd;
    });
    
    console.log(`🗓️ September 2025 flights total: ${sepFlights.length}`);
    
    // Specifically check for PNG Air (ANG organization) September flights
    const pngAirSeptFlights = sepFlights.filter(f => f.organization?.code === 'ANG');
    console.log(`✈️ PNG Air September 2025 flights: ${pngAirSeptFlights.length}`);
    
    if (pngAirSeptFlights.length > 0) {
      console.log('PNG Air September flight details:', pngAirSeptFlights.slice(0, 10).map(f => ({
        number: f.flight_number,
        departure: f.scheduled_departure,
        route: `${f.departure_city?.code || 'UNK'} → ${f.arrival_city?.code || 'UNK'}`,
        aircraft: f.aircraft_type?.code || 'UNK'
      })));
    }
    
    return sepFlights;
  }, [filteredFlights]);

  // Generate structured data for SEO
  const structuredData = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "National Airports Corporation Flight Information System",
    "description": "Real-time flight tracking system for Papua New Guinea airports",
    "url": "https://pngflightradar.com",
    "applicationCategory": "Transportation",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Real-time flight tracking",
      "Airport departure boards", 
      "Flight status updates",
      "PNG airport information"
    ]
  }), []);

  // Dynamic meta description based on flights
  const metaDescription = useMemo(() => {
    const flightCount = todaysFlights.length;
    return `Track ${flightCount} flights today across Papua New Guinea airports. Real-time departures, arrivals, and flight status updates for PNG aviation.`;
  }, [todaysFlights.length]);

  return (
    <>
      <SEOHead
        title="Find Your Flight in PNG with NAC, Runway to 50"
        description="Find Your Flight in PNG with NAC, Runway to 50"
        keywords="PNG flights, Papua New Guinea flights, flight tracker, airport departures, arrivals, flight status, PNG aviation, real-time flights"
        canonicalUrl={typeof window !== 'undefined' ? window.location.href : undefined}
        structuredData={structuredData}
      />
      
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-6 xl:px-48">

          <FlightPageHeader 
            flightCount={todaysFlights.length}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />

          {/* Main Content with Tabs - Now immediately visible */}
          {isLoading ? (
            <div className="flex justify-center items-center py-12" style={{ minHeight: '400px' }}>
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-lg">Loading flights...</span>
            </div>
          ) : (
            <FlightTabs 
              todaysFlights={todaysFlights}
              allFlights={flights}
            />
          )}

          {/* Announcement Banner */}
          <AnnouncementBanner />
          
          <div className="text-center text-sm text-muted-foreground mt-6">
            <p>Information is updated in real-time. Last update: {new Date().toLocaleTimeString()}</p>
            <OrganizationLinks />
          </div>
        </div>
      </div>
    </>
  );
}
